import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import '../styles/registeruser.css';
import { BASE_URL } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

// ===== دوال الحماية من XSS للتسجيل الدخول =====

// تنظيف المدخلات
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/[<>'"&]/g, (match) => {
      const entityMap = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;'
      };
      return entityMap[match];
    });
};

// فحص أكواد XSS
const detectXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /alert\s*\(/i,
    /confirm\s*\(/i,
    /prompt\s*\(/i,
    /document\./i,
    /window\./i,
    /eval\s*\(/i,
    /<script/i,
    /<iframe/i,
    /<object/i,
    /javascript:/i,
    /vbscript:/i,
    /on\w+\s*=/i
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

// التحقق من صحة البيانات
const validateDataType = (input, fieldName) => {
  if (typeof input !== 'string') return false;
  
  const maxLengths = { phone: 15, password: 100 };
  if (maxLengths[fieldName] && input.length > maxLengths[fieldName]) return false;
  
  const suspiciousPatterns = [
    /<[^>]*>/,
    /javascript:/i,
    /union\s+select/i,
    /drop\s+table/i,
    /\.\./
  ];
  
  return !suspiciousPatterns.some(pattern => pattern.test(input));
};

// دالة التنظيف الشاملة
const secureLoginInput = (input, fieldName) => {
  if (!input || typeof input !== 'string') return input;
  
  if (detectXSS(input)) {
    console.warn(`XSS attempt detected in login field: ${fieldName}`);
    return '';
  }
  
  if (!validateDataType(input, fieldName)) {
    console.warn(`Invalid data type in login field: ${fieldName}`);
    return '';
  }
  
  return sanitizeInput(input.trim());
};

// Validation functions
const validateField = (name, value) => {
  const errors = {};

  switch (name) {
    case 'phone':
      if (!value.trim()) {
        errors[name] = 'رقم الهاتف مطلوب';
      } else if (!/^01[0125][0-9]{8}$/.test(value.trim())) {
        errors[name] = 'يجب إدخال رقم هاتف مصري صحيح';
      }
      break;

    case 'password':
      if (!value) {
        errors[name] = 'كلمة المرور مطلوبة';
      } else if (value.length < 6) {
        errors[name] = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
      }
      break;

    default:
      break;
  }

  return errors;
};

const validateForm = (formData) => {
  const errors = {};
  
  Object.keys(formData).forEach(field => {
    const fieldErrors = validateField(field, formData[field]);
    if (fieldErrors[field]) {
      errors[field] = fieldErrors[field];
    }
  });

  return errors;
};

export const Loginuser = ({ setLoggedIn, style }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: "",
    password: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // تطبيق الحماية من XSS على المدخلات
    const securedValue = secureLoginInput(value, name);
    
    // إذا تم حذف المحتوى بسبب XSS، أظهر تحذير
    if (value && !securedValue && value.trim() !== '') {
      swal("تحذير أمني!", "تم رفض المدخلات لاحتوائها على محتوى غير آمن", "warning");
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: securedValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate field on blur
    const fieldErrors = validateField(name, value);
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    
    // فحص أمني شامل قبل الإرسال
    const securityCheck = () => {
      let hasSecurityIssue = false;
      const textFields = ['phone', 'password'];
      
      textFields.forEach(field => {
        const value = formData[field];
        if (value && typeof value === 'string') {
          if (detectXSS(value)) {
            console.error(`XSS detected in login ${field}:`, value);
            hasSecurityIssue = true;
          }
          if (!validateDataType(value, field)) {
            console.error(`Invalid data type in login ${field}:`, value);
            hasSecurityIssue = true;
          }
        }
      });
      
      return !hasSecurityIssue;
    };
    
    // تشغيل فحص الأمان
    if (!securityCheck()) {
      swal({
        title: "خطأ أمني!",
        text: "تم اكتشاف محتوى غير آمن في بيانات تسجيل الدخول.",
        icon: "error",
        button: "حسناً",
      });
      return;
    }
    
    // Validate form
    const formErrors = validateForm(formData);
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      
      swal({
        title: "خطأ في البيانات!",
        text: "يرجى مراجعة البيانات المدخلة وإصلاح الأخطاء المذكورة.",
        icon: "error",
        button: "حسناً",
      });
      
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      // تنظيف البيانات مرة أخيرة قبل الإرسال
      const sanitizedData = {
        phone: secureLoginInput(formData.phone, 'phone'),
        password: secureLoginInput(formData.password, 'password')
      };
      
      const response = await axios.post(`${BASE_URL}/api/login`, sanitizedData);
      const token = response.data.data.token;
      const role = response.data.data.user.role;
      
      localStorage.setItem("token", token);
      localStorage.setItem("sha1", role);

      swal({
        title: "تم تسجيل الدخول بنجاح! 🎉",
        icon: "success",
        button: "حسنا!",
      }).then(() => {
        setLoggedIn(true);
        navigate("/");
        window.location.reload();
      });
    } catch (e) {
      if (e.response) {
        if (e.response.status === 422) {
          setErrors(e.response.data.errors);
          setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
          
          swal({
            title: "خطأ في البيانات!",
            text: "يرجى مراجعة البيانات المدخلة وإصلاح الأخطاء المذكورة.",
            icon: "error",
            button: "حسناً",
          });
        } else if (e.response.status === 401) {
          swal({ 
            title: "خطأ في تسجيل الدخول!", 
            text: "يرجى التحقق من رقم الهاتف أو كلمة المرور", 
            icon: "error", 
            button: "أعد المحاولة" 
          });
        } else if (e.response.status === 402) {
          swal({ 
            title: "هذا الحساب محظور", 
            text: "يرجى التواصل معنا", 
            icon: "warning", 
            button: "فهمت" 
          });
        } else if (e.response.status === 403) {
          swal({ 
            title: "لا يمكنك تسجيل الدخول من جهاز آخر", 
            text: "يرجى التحقق من الجهاز", 
            icon: "warning", 
            button: "فهمت" 
          });
        } else if (e.response.status === 405) {
          swal({ 
            title: "حسابك بيتراجع دلوقتي من المساعدين", 
            text: "هيجيلك رسالة سواء بقبول الأكونت او رفضه", 
            icon: "warning", 
            button: "فهمت" 
          });
        }
      } else {
        swal({
          title: "خطأ في البيانات!",
          text: "يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.",
          icon: "error",
          button: "حسناً",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (fieldName) => {
    if (touched[fieldName] && errors[fieldName]) {
      return 'error';
    }
    if (touched[fieldName] && formData[fieldName] && !errors[fieldName]) {
      return 'success';
    }
    return 'default';
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`stylish-register-page ${style ? 'dark-mode' : ''}`}>
      <div className="form-box">
        <div className="form-panel">
          <form onSubmit={handleLogin} noValidate>
            <div className="title-wrapper">
              <h2>تسجيل <span>الدخول</span></h2>
            </div>

            <div className={`input-field ${getFieldStatus('phone')}`}>
              <input 
                id="phone" 
                type="number" 
                placeholder=" " 
                value={formData.phone} 
                name="phone"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required 
              />
              <label htmlFor="phone">رقم الهاتف</label>
              {getFieldStatus('phone') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('phone') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.phone && <small className="error-text">{errors.phone}</small>}
            </div>

            <div className={`input-field password-field ${getFieldStatus('password')}`}>
              <input 
                id="password" 
                type={showPassword ? 'text' : 'password'} 
                placeholder=" " 
                value={formData.password} 
                name="password"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required 
              />
              <label htmlFor="password">كلمة المرور</label>
              <span className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </span>
              {getFieldStatus('password') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('password') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.password && <small className="error-text">{errors.password}</small>}
            </div>
            
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <span>جاري الدخول...</span>
                </>
              ) : (
                'تسجيل الدخول'
              )}
            </button>
          </form>
        </div>
        
        <div className="welcome-panel">
          <div className="welcome-content">
            <h2>أهلاً بيك معانا!!</h2>
            <p>   لو لسه مش عامل أكونت معانا انضم إلينا الآن وابدأ رحلتك مع  Abu Ghalia .</p>
            <Link to="/registeruser" className="welcome-link">انشئ حسابك الآن!</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Loginuser;